// #region Imports
import { useState, useCallback } from 'react';
import type { MouseEvent } from 'react';
// #endregion

// #region Hook
interface UseAZCopyOptions {
  onCopy?: (error?: string) => void;
  feedbackDuration?: number;
}

interface UseAZCopyReturn {
  isCopied: boolean;
  handleCopy: (text: string | number | undefined, event: MouseEvent) => void;
}

const useAZCopy = ({ onCopy, feedbackDuration = 1500 }: UseAZCopyOptions = {}): UseAZCopyReturn => {
  // #region State
  const [isCopied, setIsCopied] = useState(false);
  // #endregion

  // #region Events
  const handleCopy = useCallback(
    (text: string | number | undefined, event: MouseEvent) => {
      event.stopPropagation();

      if (text == null || text === '') {
        onCopy?.('No text to copy');
        return;
      }

      if (!('clipboard' in navigator)) {
        onCopy?.('Clipboard API not available');
        return;
      }

      navigator.clipboard
        .writeText(String(text))
        .then(() => {
          setIsCopied(true);
          onCopy?.();
          setTimeout(() => setIsCopied(false), feedbackDuration);
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'Copy failed';
          onCopy?.(message);
        });
    },
    [onCopy, feedbackDuration],
  );
  // #endregion

  return { isCopied, handleCopy };
};
// #endregion

export { useAZCopy };
